import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, CheckCircle2, Car } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

const driverSchema = z.object({
  name: z.string().trim().min(2, "Name muss mindestens 2 Zeichen haben").max(100),
  email: z.string().trim().email("Bitte gültige E-Mail-Adresse eingeben").max(255),
  location: z.string().trim().min(2, "Bitte Ort eingeben").max(200),
  had_charging_problems: z.boolean(),
  urgency: z.number().min(1).max(5),
  newsletter_opt_in: z.boolean(),
  privacy_accepted: z.literal(true, { errorMap: () => ({ message: "Bitte Datenschutzerklärung akzeptieren" }) }),
});

const urgencyLabels = ["Nicht dringend", "Wenig", "Mittel", "Dringend", "Sehr dringend"];

const DriverWaitlist = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    location: "",
    had_charging_problems: false,
    urgency: 3,
    newsletter_opt_in: false,
    privacy_accepted: false as boolean,
  });

  const update = (field: string, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = driverSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }

    setLoading(true);
    const now = new Date().toISOString();

    const { error } = await supabase.from("drivers_waitlist").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      location: parsed.data.location,
      had_charging_problems: parsed.data.had_charging_problems,
      urgency: parsed.data.urgency,
      newsletter_opt_in: parsed.data.newsletter_opt_in,
      privacy_accepted_at: now,
    });

    if (!error && parsed.data.newsletter_opt_in) {
      await supabase.from("newsletter_subscribers").insert({
        email: parsed.data.email,
        name: parsed.data.name,
        source: "driver",
      });
    }

    setLoading(false);

    if (error) {
      toast.error("Fehler beim Speichern. Bitte versuche es erneut.");
      return;
    }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
        <div className="mx-auto max-w-md text-center space-y-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Du bist auf der Liste! 🎉</h1>
          <p className="text-muted-foreground leading-relaxed">
            Sobald Ladepunkte in deiner Nähe verfügbar sind, erfährst du es als Erster.
            Je mehr Fahrer sich anmelden, desto schneller starten wir in deiner Region.
          </p>
          <div className="rounded-xl border border-primary/15 bg-primary/5 p-4 text-sm text-primary">
            <strong>Tipp:</strong> Kennst du jemanden mit einer Wallbox? Erzähl ihm von chargeflip — so kommen Ladepunkte schneller in deine Nähe.
          </div>
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Zurück zur Startseite
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-lg py-8">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-6 gap-2">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Button>
        </Link>

        <div className="mb-8">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Car className="h-5 w-5 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Nie wieder ohne Strom stehen</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sei unter den Ersten, die Zugang zu privaten Ladepunkten in deiner Nähe bekommen — kostenlos vormerken.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name *</Label>
            <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Max Mustermann" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">E-Mail *</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="max@beispiel.de" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="location">Ort *</Label>
            <Input id="location" value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="z.B. Musterort, Bayern" required />
          </div>

          <div className="space-y-2">
            <Label>Hattest du schon Probleme beim Laden? *</Label>
            <RadioGroup
              value={form.had_charging_problems ? "yes" : "no"}
              onValueChange={(v) => update("had_charging_problems", v === "yes")}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="cp-yes" />
                <Label htmlFor="cp-yes" className="font-normal">Ja</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="cp-no" />
                <Label htmlFor="cp-no" className="font-normal">Nein</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label>Wie dringend brauchst du so einen Service? *</Label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => update("urgency", n)}
                  className={`flex-1 rounded-lg border py-2.5 text-sm font-medium transition-colors ${
                    form.urgency === n
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{urgencyLabels[0]}</span>
              <span>{urgencyLabels[4]}</span>
            </div>
          </div>

          {/* Newsletter + DSGVO */}
          <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="newsletter"
                checked={form.newsletter_opt_in}
                onCheckedChange={(v) => update("newsletter_opt_in", !!v)}
                className="mt-0.5"
              />
              <Label htmlFor="newsletter" className="font-normal text-sm leading-relaxed cursor-pointer">
                Ja, ich möchte den chargeflip-Newsletter erhalten und über Neuigkeiten, den Launch und Tipps informiert werden. Abmeldung jederzeit möglich.
              </Label>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="privacy"
                checked={form.privacy_accepted}
                onCheckedChange={(v) => update("privacy_accepted", !!v)}
                className="mt-0.5"
              />
              <Label htmlFor="privacy" className="font-normal text-sm leading-relaxed cursor-pointer">
                Ich habe die{" "}
                <a href="/datenschutz" target="_blank" className="text-primary underline hover:no-underline">
                  Datenschutzerklärung
                </a>{" "}
                gelesen und stimme der Verarbeitung meiner Daten zu. *
              </Label>
            </div>
          </div>

          <Button type="submit" className="w-full rounded-full shadow-sm shadow-primary/20" size="lg" disabled={loading || !form.privacy_accepted}>
            {loading ? "Wird gespeichert…" : "Kostenlos vormerken"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default DriverWaitlist;
